import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, Mail, MapPin, Loader2 } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const settings = useSettings();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.message) {
      toast.error("Vul alstublieft alle velden in.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, type: "offerte" });
      toast.success("Bedankt! We nemen zo snel mogelijk contact met u op.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error("Er ging iets mis. Probeer het later opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
        <Reveal>
          <span className="text-[#FF1A1A] text-sm font-semibold tracking-[0.25em] uppercase">
            Contact
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mt-5 leading-tight">
            Laten we uw project <br /> bespreken.
          </h2>
          <p className="text-white/60 mt-6 font-light leading-relaxed max-w-md">
            Heeft u vragen of wilt u een vrijblijvende offerte? Neem direct
            contact op via telefoon, WhatsApp of het formulier.
          </p>

          <div className="flex flex-col gap-4 mt-9">
            <a href={`tel:${settings.phoneRaw}`} data-testid="contact-phone-link" className="flex items-center gap-4 text-white/80 hover:text-white transition-colors">
              <span className="w-11 h-11 bg-[#111] border border-white/10 flex items-center justify-center"><Phone size={18} className="text-[#FF1A1A]" /></span>
              {settings.phone}
            </a>
            <a href={`mailto:${settings.email}`} data-testid="contact-email-link" className="flex items-center gap-4 text-white/80 hover:text-white transition-colors">
              <span className="w-11 h-11 bg-[#111] border border-white/10 flex items-center justify-center"><Mail size={18} className="text-[#FF1A1A]" /></span>
              {settings.email}
            </a>
            <div className="flex items-center gap-4 text-white/80">
              <span className="w-11 h-11 bg-[#111] border border-white/10 flex items-center justify-center"><MapPin size={18} className="text-[#FF1A1A]" /></span>
              {settings.location}
            </div>
          </div>

          <div className="flex gap-3 mt-8">
            <a href={`https://wa.me/${settings.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-button" className="bg-[#25D366] text-white px-6 py-3 font-semibold flex items-center gap-2 hover:opacity-90 transition-opacity">
              <MessageCircle size={18} /> WhatsApp
            </a>
            <a href={`tel:${settings.phoneRaw}`} data-testid="contact-call-button" className="bg-white text-black px-6 py-3 font-semibold flex items-center gap-2 hover:bg-gray-200 transition-colors">
              <Phone size={18} /> Bel direct
            </a>
          </div>

          <div className="mt-10">
            <p className="text-white/40 text-xs tracking-[0.2em] uppercase mb-4">Werkgebied</p>
            <div className="flex flex-wrap gap-2">
              {(settings.work_areas || []).map((a) => (
                <span key={a} data-testid={`work-area-${a}`} className="text-sm text-white/70 border border-white/10 px-4 py-1.5 hover:border-[#FF1A1A] hover:text-white transition-colors">
                  {a}
                </span>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-[#111111] p-8 md:p-10 border border-white/5"
          >
            <h3 className="font-heading text-2xl font-bold text-white mb-7">Gratis offerte aanvragen</h3>
            <div className="flex flex-col gap-6">
              <input name="name" value={form.name} onChange={handle} placeholder="Naam" data-testid="contact-input-name" className="w-full bg-transparent border-b border-white/20 focus:border-[#FF1A1A] outline-none py-3 text-white transition-colors placeholder:text-white/30" />
              <input name="phone" value={form.phone} onChange={handle} placeholder="Telefoon" data-testid="contact-input-phone" className="w-full bg-transparent border-b border-white/20 focus:border-[#FF1A1A] outline-none py-3 text-white transition-colors placeholder:text-white/30" />
              <input name="email" type="email" value={form.email} onChange={handle} placeholder="E-mail" data-testid="contact-input-email" className="w-full bg-transparent border-b border-white/20 focus:border-[#FF1A1A] outline-none py-3 text-white transition-colors placeholder:text-white/30" />
              <textarea name="message" value={form.message} onChange={handle} placeholder="Bericht" rows={4} data-testid="contact-input-message" className="w-full bg-transparent border-b border-white/20 focus:border-[#FF1A1A] outline-none py-3 text-white transition-colors placeholder:text-white/30 resize-none" />
              <button type="submit" disabled={loading} data-testid="contact-submit-button" className="bg-[#FF1A1A] text-white px-8 py-4 font-semibold hover:bg-[#E60000] transition-colors flex items-center justify-center gap-2 mt-2 disabled:opacity-60">
                {loading ? <><Loader2 size={18} className="animate-spin" /> Versturen...</> : "Verstuur aanvraag"}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
